import { accountService, type ShiftEvent } from '@libs/data-access/queries'
import { Anchor, Badge, Tooltip } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { useFallbackImageErrorCheck } from '@spa/scenes/TimeClockReport/hooks/useFallbackImageErrorCheck'
import type { FC } from 'react'
import { useState } from 'react'

import styles from './SignAndPictureControlBadges.module.css'

type ControlBadgesProps = {
  event: ShiftEvent
}

export const SignAndPictureControlBadges: FC<ControlBadgesProps> = ({
  event
}) => {
  const { t } = usePlanningFormTranslation()

  const [isSignatureImageError, setIsSignatureImageError] = useState(false)
  const [isPhotoImageError, setIsPhotoImageError] = useState(false)
  const { isImageFallbackError } = useFallbackImageErrorCheck(event?.photo_url)
  const accountQuery = accountService.useFetchCurrent()

  const hasPhotoEnabled =
    accountQuery.data?.timeclock_preferences.has_photo_enabled ?? false

  const isDeleted = Boolean(event.photo_deleted_at)
  const hasPhotoIssue =
    !isDeleted && (Boolean(event.photo_issue) || isImageFallbackError)
  const getSignatureTooltipContent = () => {
    if (isSignatureImageError) {
      return t('shiftRealHoursForm.controlBadges.signature')
    }
    return (
      <div className={styles.TooltipBackground}>
        <img
          src={event.image_url}
          alt={t('shiftRealHoursForm.controlBadges.emplyeesSignature')}
          className={styles.TooltipImage}
          onError={() => setIsSignatureImageError(true)}
        />
      </div>
    )
  }

  const getPhotoTooltipContent = () => {
    switch (true) {
      case isDeleted:
        return t('shiftRealHoursForm.controlBadges.deletedPhoto')
      case hasPhotoIssue:
        return t('shiftRealHoursForm.controlBadges.photoIssue')
      case isPhotoImageError:
        return t('shiftRealHoursForm.controlBadges.importPhoto')
      default:
        return (
          <div className={styles.TooltipBackground}>
            <img
              src={event.photo_url}
              alt={t('shiftRealHoursForm.controlBadges.portrait')}
              className={styles.TooltipImage}
              onError={() => setIsPhotoImageError(true)}
            />
          </div>
        )
    }
  }

  return (
    <div className='flex'>
      {event.image_url && (
        <Anchor
          href={event.image_url}
          rel='noopener noreferrer'
          target='_blank'
        >
          <Tooltip
            id={`signature_${event.id}`}
            label={getSignatureTooltipContent()}
            position='left'
          >
            <Badge
              label={t('shiftRealHoursForm.controlBadges.signatureLabel')}
              size='s'
            />
          </Tooltip>
        </Anchor>
      )}
      {hasPhotoEnabled &&
        (event.photo_url || event.photo_issue || isDeleted) && (
          <Anchor
            rel='noopener noreferrer'
            target='_blank'
            {...(event.photo_url && { href: event.photo_url })}
          >
            <Tooltip
              id={`photo_${event.id}`}
              label={getPhotoTooltipContent()}
              position='left'
            >
              <Badge
                label={t('shiftRealHoursForm.controlBadges.photoLabel')}
                size='s'
              />
            </Tooltip>
          </Anchor>
        )}
    </div>
  )
}
